import { ApiError, getValidationError } from '@/shared/lib/problem-details';
import { formatCurrency } from '@/shared/lib/money';
import { formatDayMonth } from './plans';
import type {
  AiUnitActiveAlert,
  AiUnitLot,
  AiUnitsCycle,
  AiUnitsSummary,
  MetaCostEstimate,
} from '../types/billing.types';

/* ==========================================================================
   Atendimentos no painel (change billing-ai-units)

   Os números vêm todos de `GET /billing/ai-units`; aqui só se escrevem as
   frases. Datas no calendário de São Paulo, o mesmo do Billing.
   ========================================================================== */

/** "1.940". */
export function formatUnits(value: number): string {
  return value.toLocaleString('pt-BR');
}

/** "+300" ou "−23", com o sinal de menos tipográfico. */
export function signedUnits(units: number): string {
  if (units > 0) return `+${formatUnits(units)}`;
  if (units < 0) return `−${formatUnits(Math.abs(units))}`;
  return '0';
}

/** "US$ 13,19". */
export function formatUsd(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
}

/** "~1.940 mensagens ≈ US$ 13,19 (≈ R$ 71,10)", ou só a contagem sem tarifa. */
export function metaEstimateText(estimate: MetaCostEstimate): string {
  const count = estimate.billableMessages;
  const messages = `~${formatUnits(count)} ${count === 1 ? 'mensagem' : 'mensagens'}`;
  if (estimate.estimatedUsd === null) return messages;
  const brl = estimate.estimatedBrl !== null ? ` (≈ ${formatCurrency(estimate.estimatedBrl)})` : '';
  return `${messages} ≈ ${formatUsd(estimate.estimatedUsd)}${brl}`;
}

/** "1 atendimento" / "3 atendimentos". */
export function unitsText(value: number): string {
  return `${formatUnits(value)} ${value === 1 ? 'atendimento' : 'atendimentos'}`;
}

const saoPauloIsoDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const saoPauloYear = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric' });

/** O dia (AAAA-MM-DD) de um instante em São Paulo. */
export function saoPauloDate(instant: Date): string {
  return saoPauloIsoDate.format(instant);
}

/** "DD/MM/AAAA" de uma data sem hora ("AAAA-MM-DD"), sem passar por fuso. */
export function formatDateOnly(date: string): string {
  const [year, month, day] = date.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : date;
}

/** "DD/MM" no ano corrente e "DD/MM/AAAA" fora dele: um pacote vale 12 meses. */
export function formatExpiryDay(instant: string, now: Date = new Date()): string {
  const sameYear = saoPauloYear.format(new Date(instant)) === saoPauloYear.format(now);
  return sameYear ? formatDayMonth(instant) : formatDateOnly(saoPauloDate(new Date(instant)));
}

/** "212 de 300 usados · renova em 14/10". */
export function cycleText(cycle: AiUnitsCycle): string {
  return `${formatUnits(cycle.used)} de ${formatUnits(cycle.total)} usados · renova em ${formatDayMonth(cycle.endsAt)}`;
}

/** Percentual usado do ciclo, de 0 a 100, para a barra. */
export function cyclePercent(cycle: AiUnitsCycle): number {
  if (cycle.total <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((cycle.used / cycle.total) * 100)));
}

const PACK_SOURCES = new Set<AiUnitLot['source']>(['purchase', 'concession']);

/** O vencimento mais próximo entre os pacotes e cortesias com sobra, e quanto vence nele. */
export function nextPackExpiry(lots: AiUnitLot[]): { at: string; units: number } | null {
  const withUnits = lots.filter((lot) => PACK_SOURCES.has(lot.source) && lot.remainingUnits > 0);
  if (withUnits.length === 0) return null;
  const earliest = withUnits.reduce((min, lot) => (Date.parse(lot.expiresAt) < Date.parse(min) ? lot.expiresAt : min), withUnits[0]!.expiresAt);
  const day = saoPauloDate(new Date(earliest));
  const units = withUnits
    .filter((lot) => saoPauloDate(new Date(lot.expiresAt)) === day)
    .reduce((sum, lot) => sum + lot.remainingUnits, 0);
  return { at: earliest, units };
}

/** "100 de pacote, vencem em 12/03" ou "100 de pacote, 60 vencem em 12/03"; nulo sem pacote. */
export function packText(summary: Pick<AiUnitsSummary, 'packUnits' | 'lots'>, now: Date = new Date()): string | null {
  if (summary.packUnits <= 0) return null;
  const expiry = nextPackExpiry(summary.lots);
  const total = `${formatUnits(summary.packUnits)} de pacote`;
  if (!expiry) return total;
  const day = formatExpiryDay(expiry.at, now);
  if (expiry.units >= summary.packUnits) {
    return `${total}, ${summary.packUnits === 1 ? 'vence' : 'vencem'} em ${day}`;
  }
  return `${total}, ${formatUnits(expiry.units)} ${expiry.units === 1 ? 'vence' : 'vencem'} em ${day}`;
}

/** Dias de calendário de hoje (São Paulo) até `date` (AAAA-MM-DD). */
export function daysUntil(date: string, now: Date = new Date()): number {
  const today = Date.parse(`${saoPauloDate(now)}T00:00:00Z`);
  const target = Date.parse(`${date.slice(0, 10)}T00:00:00Z`);
  return Math.round((target - today) / 86_400_000);
}

/**
 * A previsão no ritmo dos últimos 7 dias, ou nulo sem consumo neles:
 * "No ritmo atual, acaba em ~6 dias." ou "No ritmo atual, deve durar até a renovação."
 */
export function forecastText(summary: Pick<AiUnitsSummary, 'forecast' | 'state'>, now: Date = new Date()): string | null {
  if (summary.state === 'exhausted' || !summary.forecast) return null;
  const { exhaustsOn } = summary.forecast;
  if (!exhaustsOn) return 'No ritmo atual, deve durar até a renovação.';
  const days = daysUntil(exhaustsOn, now);
  if (days <= 0) return 'No ritmo atual, acaba hoje.';
  return `No ritmo atual, acaba em ~${days} ${days === 1 ? 'dia' : 'dias'}.`;
}

/** "Carência: 3 atendimentos serão descontados do próximo lote."; nulo sem dívida. */
export function graceText(graceDebt: number): string | null {
  if (graceDebt <= 0) return null;
  return graceDebt === 1
    ? 'Carência: 1 atendimento será descontado do próximo lote.'
    : `Carência: ${formatUnits(graceDebt)} atendimentos serão descontados do próximo lote.`;
}

/** O reembolso do pacote, como o diálogo de compra o promete. */
export const AI_UNITS_REFUND_TEXT =
  'Reembolso integral em até 7 dias se nenhum atendimento do pacote for usado, pelo suporte; atendimentos de carência quitados pelo pacote contam como uso.';

/** O que acontece sem saldo, nas palavras do dono. */
export const EXHAUSTED_TEXT =
  'Atendente IA sem saldo: novos clientes recebem uma mensagem automática com o link do cardápio e ficam com a equipe';

/** Os textos da faixa de quem tem `billing.read`. */
export const AI_UNITS_ALERT_TEXT: Record<AiUnitActiveAlert, string> = {
  cycle_80: 'Você já usou 80% dos atendimentos do mês',
  cycle_100: 'Os atendimentos do mês acabaram; o atendente está usando seus pacotes',
  exhausted: EXHAUSTED_TEXT,
};

/** A faixa da equipe (`conversations.read` sem `billing.read`): sem números nem compra. */
export const AI_UNITS_TEAM_TEXT =
  "Atendente IA sem saldo: as conversas novas chegam para a equipe com o motivo 'sem saldo'. Avise o dono.";

/** Só os avisos do ciclo se dispensam; o sem saldo fica até o saldo voltar. */
export function isDismissibleAlert(alert: AiUnitActiveAlert): boolean {
  return alert !== 'exhausted';
}

/* ---- Faixa dispensada por ciclo, em estado local do navegador ---- */

const DISMISS_STORAGE_PREFIX = 'vapt-ai-units-banner:';

interface DismissedState {
  cycle: string;
  alerts: AiUnitActiveAlert[];
}

/** A chave do ciclo: o início dele, ou "none" num ciclo sem cota. */
export function cycleKey(cycle: AiUnitsCycle | null): string {
  return cycle?.startsAt ?? 'none';
}

function readDismissed(tenantId: string): DismissedState | null {
  try {
    const raw = globalThis.localStorage?.getItem(`${DISMISS_STORAGE_PREFIX}${tenantId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DismissedState>;
    if (typeof parsed.cycle !== 'string' || !Array.isArray(parsed.alerts)) return null;
    return { cycle: parsed.cycle, alerts: parsed.alerts.filter((a): a is AiUnitActiveAlert => typeof a === 'string') };
  } catch {
    // Armazenamento bloqueado, cheio ou com lixo: a faixa volta a aparecer.
    return null;
  }
}

/** O aviso foi dispensado neste ciclo, neste navegador. */
export function isAlertDismissed(tenantId: string, cycle: string, alert: AiUnitActiveAlert): boolean {
  if (!isDismissibleAlert(alert)) return false;
  const stored = readDismissed(tenantId);
  return !!stored && stored.cycle === cycle && stored.alerts.includes(alert);
}

/** Dispensa o aviso até o ciclo seguinte; o ciclo novo apaga os anteriores. */
export function dismissAlert(tenantId: string, cycle: string, alert: AiUnitActiveAlert): void {
  if (!isDismissibleAlert(alert)) return;
  const stored = readDismissed(tenantId);
  const alerts = stored && stored.cycle === cycle ? stored.alerts : [];
  const next: DismissedState = { cycle, alerts: Array.from(new Set([...alerts, alert])) };
  try {
    globalThis.localStorage?.setItem(`${DISMISS_STORAGE_PREFIX}${tenantId}`, JSON.stringify(next));
  } catch {
    // Sem armazenamento, a dispensa vale só enquanto a página está aberta.
  }
}

/* ---- Recusas da compra ---- */

export const AI_UNIT_ERROR_CODES = {
  planRequired: 'billing.ai_units_plan_required',
  packNotFound: 'billing.ai_units_pack_not_found',
  gatewayUnavailable: 'billing.gateway_unavailable',
  keyReused: 'billing.idempotency_key_reused',
} as const;

export type AiUnitPurchaseFailureKind =
  | 'plan_required'
  | 'pack_not_found'
  | 'gateway'
  | 'key_reused'
  | 'billing_info'
  | 'forbidden'
  | 'network'
  | 'other';

export interface AiUnitPurchaseFailure {
  kind: AiUnitPurchaseFailureKind;
  message: string;
}

function errorCodeOf(error: ApiError): string | null {
  const code = error.problem.errorCode;
  return typeof code === 'string' ? code : null;
}

/** Algum erro de campo é dos dados de cobrança (`billingInfo` ou `billingInfo.*`). */
function billingInfoError(error: ApiError): string | null {
  const direct = getValidationError(error.problem, 'billingInfo');
  if (direct) return direct;
  const nested = Object.entries(error.problem.errors ?? {}).find(([key]) =>
    key.toLocaleLowerCase('en-US').startsWith('billinginfo.'));
  return nested?.[1]?.[0] ?? null;
}

/** Mensagem da compra que não saiu. Falha do gateway nunca mostra o texto do provedor. */
export function getAiUnitPurchaseFailure(error: unknown): AiUnitPurchaseFailure {
  if (!(error instanceof ApiError)) {
    return { kind: 'other', message: 'Não foi possível concluir a compra. Tente novamente.' };
  }
  const code = errorCodeOf(error);

  if (error.status === 0) {
    return {
      kind: 'network',
      message: 'Sem conexão com o servidor. Confira a internet e tente de novo: a mesma compra é retomada, sem cobrança em dobro.',
    };
  }
  if (error.status === 502 || error.status === 503 || code === AI_UNIT_ERROR_CODES.gatewayUnavailable) {
    return {
      kind: 'gateway',
      message: 'O provedor de pagamentos está indisponível agora. Tente de novo em instantes: a mesma compra é retomada, sem cobrança em dobro.',
    };
  }
  if (code === AI_UNIT_ERROR_CODES.planRequired) {
    return {
      kind: 'plan_required',
      message: 'Pacotes de atendimentos são do plano "Vapt Menu + Atendente IA". Mude de plano para comprar.',
    };
  }
  if (code === AI_UNIT_ERROR_CODES.packNotFound) {
    return { kind: 'pack_not_found', message: 'Este pacote não está mais à venda. Atualizamos a lista: escolha um pacote de novo.' };
  }
  if (code === AI_UNIT_ERROR_CODES.keyReused) {
    return { kind: 'key_reused', message: 'Esta tentativa já foi usada com outra escolha. Confirme a compra de novo.' };
  }
  if (error.status === 400) {
    const billing = billingInfoError(error);
    if (billing) return { kind: 'billing_info', message: billing };
  }
  if (error.status === 403) {
    return { kind: 'forbidden', message: 'Só o dono da loja pode comprar atendimentos.' };
  }
  return { kind: 'other', message: 'Não foi possível concluir a compra. Tente novamente.' };
}
